import { c as _c } from "react/compiler-runtime";
import { Event } from '../../../../../../../../typings/Event.interface';
import { EventAttempt } from '../../../../../../../../typings/EventAttempt.interface';
import { APIWebhook } from '../../../../../../../../typings/Webhook.interface';
import { numberWithCommas } from '../../../../../utils';
import { ATTEMPT_TRIGGER_LABELS } from '../../../../../utils/attempt';
import Badge from '../../../../common/base/Badge';
import Button from '../../../../common/base/Button';
import Chip from '../../../../common/base/Chip';
import { IconName } from '../../../../common/base/Icon';
import Text from '../../../../common/base/Text';
import DisplayDate from '../../../../common/DisplayDate';
import DropdownMenu, { DropdownMenuOption } from '../../../../common/DropdownMenu';
import { Div } from '../../../../common/helpers/StyledUtils';
import { Status } from '../../../../common/Status';
interface EventListLayout {
  [k: string]: () => {
    widths: number[] | {
      min?: number;
      max?: number;
    }[];
    headers: (string | JSX.Element)[];
    getEventRowFields: (event: Event, outdated: boolean, actions: {
      retry: (event: Event) => void;
      bookmark: (event: Event) => void;
      cancel: (event: Event) => void;
      replay: (event: Event) => void;
    }, webhook?: APIWebhook, cli_client?: any) => (string | JSX.Element | object)[];
    getAttemptRowFields: (attempt: EventAttempt, outdated: boolean, actions: {
      retry: (event: Event) => void;
    }) => (string | JSX.Element | object)[];
  };
}
const ActionButtons = t0 => {
  const $ = _c(20);
  const {
    event,
    actions
  } = t0;
  const can_retry = event.status !== "QUEUED" && event.state !== "NONE";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["HOLD", "SCHEDULED", "QUEUED"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const can_cancel = t1.includes(event.status);
  let dropdown_options;
  if ($[1] !== actions || $[2] !== can_cancel || $[3] !== can_retry || $[4] !== event) {
    dropdown_options = [];
    if (can_retry) {
      let t2;
      if ($[6] !== actions || $[7] !== event) {
        t2 = {
          icon: "retry",
          label: "Retry delivery",
          tooltip: "Retry delivery of this event",
          onClick: e => {
            e.stopPropagation();
            actions.retry(event);
          }
        };
        $[6] = actions;
        $[7] = event;
        $[8] = t2;
      } else {
        t2 = $[8];
      }
      dropdown_options.push(t2);
    }
    if (can_cancel) {
      let t2;
      if ($[9] !== actions || $[10] !== event) {
        t2 = {
          icon: "cancel",
          label: "Cancel delivery",
          danger: true,
          onClick: e_0 => {
            e_0.stopPropagation();
            actions.cancel(event);
          }
        };
        $[9] = actions;
        $[10] = event;
        $[11] = t2;
      } else {
        t2 = $[11];
      }
      dropdown_options.push(t2);
    }
    $[1] = actions;
    $[2] = can_cancel;
    $[3] = can_retry;
    $[4] = event;
    $[5] = dropdown_options;
  } else {
    dropdown_options = $[5];
  }
  if (dropdown_options.length === 0) {
    return null;
  }
  if (dropdown_options.length === 1) {
    const option = dropdown_options[0];
    const t2 = option.icon as IconName;
    let t3;
    if ($[12] !== option) {
      t3 = e_1 => {
        e_1.stopPropagation();
        option.onClick?.(e_1 as React.MouseEvent<HTMLButtonElement>);
      };
      $[12] = option;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    let t4;
    if ($[14] !== t2 || $[15] !== t3) {
      t4 = <Button.Permission role="member" ghost={true} icon={t2} muted={true} small={true} onClick={t3} />;
      $[14] = t2;
      $[15] = t3;
      $[16] = t4;
    } else {
      t4 = $[16];
    }
    return t4;
  }
  let t2;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      px: 192
    };
    $[17] = t2;
  } else {
    t2 = $[17];
  }
  let t3;
  if ($[18] !== dropdown_options) {
    t3 = <DropdownMenu renderToggle={_temp} w={t2} portal={true} options={dropdown_options} />;
    $[18] = dropdown_options;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  return t3;
};
const event_list_layouts: EventListLayout = {
  cli: () => ({
    widths: [{
      min: 180,
      max: 180
    }, {
      min: 140,
      max: 140
    }, {
      min: 200
    }, {
      min: 128,
      max: 128
    }, {
      min: 172,
      max: 172
    }],
    headers: ['Event Date', 'Status', 'Connection', 'Attempts', 'Device Name'],
    getEventRowFields: (event, outdated, actions, webhook, client) => [event.created_at && <DisplayDate date={event.created_at} show_timezone={false} size="xs" />, <Div key={`${event.id}-status`} flex={{
      gap: 2,
      justify: 'space-between',
      align: 'center'
    }}>
        <Status key={event.id} {...event} status={event.status} />
        <ActionButtons event={event} actions={actions} />
      </Div>, webhook ? <Chip to={`/connections/${webhook.id}`} outline mono label={webhook.full_name} /> : <Text muted size="s">
          Connection deleted
        </Text>, `${event.attempts || 0}`, client?.device_name],
    getAttemptRowFields: attempt => [<DisplayDate key={`${attempt.id}-date`} date={attempt.created_at} show_timezone={false} size="xs" />, <Status key={`${attempt.id}-status`} {...attempt} />, <Badge key={`${attempt.id}-trigger`} neutral>
        {ATTEMPT_TRIGGER_LABELS[attempt.trigger]}
      </Badge>, <Text key={`${attempt.id}-delivery`} size="xs" mono>
        {!attempt.delivery_latency ? '-' : `${numberWithCommas(attempt.delivery_latency)}ms`}
      </Text>, <Text key={`${attempt.id}-response`} size="xs" mono>
        {!attempt.response_latency ? '-' : `${numberWithCommas(attempt.response_latency)}ms`}
      </Text>]
  }),
  http: () => ({
    widths: [{
      min: 180,
      max: 180
    }, {
      min: 140,
      max: 140
    }, {
      min: 200
    }, {
      min: 128,
      max: 128
    }, {
      min: 188,
      max: 188
    }],
    headers: ['Event Date', 'Status', 'Connection', 'Attempts', 'Next Scheduled Attempt'],
    getEventRowFields: (event, outdated, actions, webhook) => [event.created_at && <DisplayDate date={event.created_at} show_timezone={false} size="xs" />, <Div key={`${event.id}-status`} flex={{
      gap: 2,
      justify: 'space-between',
      align: 'center'
    }}>
        <Status key={event.id} {...event} status={event.status} />
        <ActionButtons event={event} actions={actions} />
      </Div>, webhook ? <Div flex={{
      justify: 'flex-start',
      align: 'center'
    }}>
          <Chip to={`/connections/${webhook.id}`} outline mono label={webhook.full_name} p={{
        x: 1
      }} />
        </Div> : <Text muted size="s">
          Connection deleted
        </Text>, `${event.attempts || 0}`, event.next_attempt_at && <Div key={`${event.id}-next-attempt`} flex={{
      gap: 2,
      justify: 'space-between',
      align: 'center'
    }}>
          <DisplayDate date={event.next_attempt_at} show_timezone={false} size="xs" />
          <Button.Permission role="member" ghost icon="cancel" muted small onClick={e => {
        e.stopPropagation();
        actions.cancel(event);
      }} />
        </Div>],
    getAttemptRowFields: attempt => [<DisplayDate key={`${attempt.id}-date`} date={attempt.created_at} show_timezone={false} size="xs" />, <Status key={`${attempt.id}-status`} {...attempt} />, <Badge key={`${attempt.id}-trigger`} neutral>
        {ATTEMPT_TRIGGER_LABELS[attempt.trigger]}
      </Badge>, <Text key={`${attempt.id}-delivery`} muted size="xs" mono>
        {!attempt.delivery_latency ? '-' : `${numberWithCommas(attempt.delivery_latency)}ms`}
      </Text>, <Text key={`${attempt.id}-response`} muted size="xs" mono>
        {!attempt.response_latency ? '-' : `${numberWithCommas(attempt.response_latency)}ms`}
      </Text>]
  })
};
export default event_list_layouts;
function _temp(opened, toggle) {
  return <Button.Permission role="member" ghost={true} active={opened} icon="horizontal_more" m={{
    t: 1
  }} muted={true} small={true} onClick={e_2 => {
    e_2.stopPropagation();
    toggle(!opened);
  }} />;
}